/** Число и форма слова при нём — часть словаря, а не помощник рядом с экраном.
 *
 *  Русскому нужны три формы, английскому — две. Это разное **устройство**, а не
 *  разные строки, и подставить одну вместо другой нельзя. Поэтому выбор формы
 *  делает не таблица в компоненте (так было в `DeleteModal.tsx`: три варианта,
 *  зашитых по-русски), а правило языка — `Intl.PluralRules`. Оно само знает,
 *  сколько категорий у языка: `ru` даёт one/few/many, `en` — one/other.
 *
 *  Второго механизма перевода при этом не заводится (технический инвариант 6):
 *  формы объявлены в тех же `ru.ts` и `en.ts`, что и остальные строки, каждая на
 *  своём языке и в своём числе. Экран получает готовую функцию `(n) => строка`
 *  и о категориях не знает вовсе — ему незачем.
 */
import type { Language } from "./index";

/** Категории, которыми пользуются наши языки. `two`, `zero` и `other` сверх
 *  перечисленных здесь не нужны: их нет ни у русского, ни у английского. */
export type PluralForms = {
  one: string;
  few?: string;
  many?: string;
  other?: string;
};

// Правило языка стоит денег на создании и ничего не стоит на вызове.
const RULES = new Map<Language, Intl.PluralRules>();

function rules(lang: Language): Intl.PluralRules {
  let rule = RULES.get(lang);
  if (!rule) {
    rule = new Intl.PluralRules(lang);
    RULES.set(lang, rule);
  }
  return rule;
}

/** Форма слова при числе `n` по правилам языка `lang`.
 *
 *  Запасной путь — `one`: он есть у обоих языков, и промолчать здесь хуже, чем
 *  показать неточную форму. */
export function plural(lang: Language, n: number, forms: PluralForms): string {
  const category = rules(lang).select(n) as keyof PluralForms;
  return forms[category] ?? forms.other ?? forms.one;
}

/** Русские формы одной строкой: «1 проект», «2 проекта», «5 проектов». */
export function ruPlural(one: string, few: string, many: string) {
  return (n: number): string => plural("ru", n, { one, few, many });
}

/** Английские формы: «1 project», «2 projects». */
export function enPlural(one: string, other: string) {
  return (n: number): string => plural("en", n, { one, other });
}
